import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Check, Loader2, Play, Settings, Square, User, UserCircle, X } from 'lucide-react';
import { cn, ui } from '../../utils';

interface Voice {
  id: string;
  name: string;
  style: string;
  gender: 'male' | 'female';
}

interface VoicePickerProps {
  className?: string;
}

const STORAGE_KEY = 'ally:selected-voice-id';
const DEFAULT_VOICE_ID = '21m00Tcm4TlvDq8ikWAM';
const PREVIEW_TEXT = 'Hi, I am your AI assistant at AFN. How can I help you today?';

function storageGet(key: string) {
  return typeof localStorage !== 'undefined' && typeof localStorage.getItem === 'function'
    ? localStorage.getItem(key)
    : null;
}

function storageSet(key: string, value: string) {
  if (typeof localStorage !== 'undefined' && typeof localStorage.setItem === 'function') {
    localStorage.setItem(key, value);
  }
}

export function getSelectedVoiceId() {
  return storageGet(STORAGE_KEY) || DEFAULT_VOICE_ID;
}

export function VoicePicker({ className }: VoicePickerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [voices, setVoices] = useState<Voice[]>([]);
  const [isLoadingVoices, setIsLoadingVoices] = useState(false);
  const [selectedVoiceId, setSelectedVoiceId] = useState<string>(() => getSelectedVoiceId());
  const [playingVoiceId, setPlayingVoiceId] = useState<string | null>(null);
  const [loadingPreview, setLoadingPreview] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const audioUrlRef = useRef<string | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const stopPreview = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    if (audioUrlRef.current) {
      URL.revokeObjectURL(audioUrlRef.current);
      audioUrlRef.current = null;
    }
    setPlayingVoiceId(null);
    setLoadingPreview(null);
  };

  useEffect(() => {
    if (!isOpen) return;
    const handleClick = (event: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        stopPreview();
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    let cancelled = false;

    const loadVoices = async () => {
      setIsLoadingVoices(true);
      try {
        const response = await fetch('/api/voice/voices', { credentials: 'include' });
        const payload = await response.json();
        if (!response.ok || !payload?.success) {
          throw new Error(payload?.error || 'Failed to load voices');
        }
        if (!cancelled) {
          setVoices(Array.isArray(payload.data) ? payload.data : []);
        }
      } catch (error) {
        console.error('Failed to load voices:', error);
        if (!cancelled) {
          setVoices([]);
        }
      } finally {
        if (!cancelled) {
          setIsLoadingVoices(false);
        }
      }
    };

    void loadVoices();

    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  useEffect(() => {
    if (voices.length === 0) return;

    const fallbackVoiceId = voices.find((voice) => voice.id === DEFAULT_VOICE_ID)?.id || voices[0]?.id;
    if (!fallbackVoiceId) return;

    if (!voices.some((voice) => voice.id === selectedVoiceId)) {
      setSelectedVoiceId(fallbackVoiceId);
      storageSet(STORAGE_KEY, fallbackVoiceId);
    }
  }, [selectedVoiceId, voices]);

  useEffect(() => () => stopPreview(), []);

  const playPreview = async (voiceId: string) => {
    if (playingVoiceId === voiceId) {
      stopPreview();
      return;
    }

    stopPreview();
    setLoadingPreview(voiceId);

    try {
      const response = await fetch('/api/voice/synthesize', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ text: PREVIEW_TEXT, voice: voiceId }),
      });

      if (!response.ok) {
        throw new Error('Preview failed');
      }

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const audio = new Audio(url);
      audioRef.current = audio;
      audioUrlRef.current = url;
      setLoadingPreview(null);
      setPlayingVoiceId(voiceId);

      const cleanup = () => {
        if (audioRef.current === audio) {
          audioRef.current = null;
        }
        if (audioUrlRef.current === url) {
          URL.revokeObjectURL(url);
          audioUrlRef.current = null;
        }
        setPlayingVoiceId(null);
      };

      audio.onended = cleanup;
      audio.onerror = cleanup;
      await audio.play();
    } catch (error) {
      console.error('Preview error:', error);
      stopPreview();
    }
  };

  const selectVoice = (voiceId: string) => {
    setSelectedVoiceId(voiceId);
    storageSet(STORAGE_KEY, voiceId);
  };

  const selectedVoiceName = useMemo(
    () => voices.find((voice) => voice.id === selectedVoiceId)?.name || 'selected',
    [selectedVoiceId, voices],
  );

  return (
    <div className={cn('relative', className)} ref={panelRef}>
      <button
        type="button"
        onClick={() => setIsOpen((open) => !open)}
        className={cn(
          ui.button.icon,
          isOpen && 'bg-blue-500/10 text-blue-300',
        )}
        title="Voice settings"
        aria-label="Choose voice settings"
        aria-haspopup="dialog"
        aria-expanded={isOpen}
      >
        <Settings className="h-4 w-4" />
      </button>

      {isOpen ? (
        <div className="absolute bottom-full right-0 z-50 mb-2 flex max-h-[420px] w-80 flex-col overflow-hidden rounded-[1.25rem] border border-border bg-card shadow-2xl shadow-black/30" role="dialog" aria-label="Choose voice">
          <div className="flex items-center justify-between border-b border-border bg-background px-4 py-3">
            <h3 className="text-sm font-semibold text-foreground">Choose Voice</h3>
            <button
              type="button"
              onClick={() => {
                setIsOpen(false);
                stopPreview();
              }}
              className={cn(ui.button.icon, 'h-9 w-9 border-0')}
              aria-label="Close voice picker"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          <div className="flex-1 space-y-1 overflow-y-auto p-2">
            {isLoadingVoices ? (
              <div className="flex items-center justify-center gap-2 py-8 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading voices...
              </div>
            ) : voices.length > 0 ? (
              voices.map((voice) => {
                const isSelected = selectedVoiceId === voice.id;
                const isPlaying = playingVoiceId === voice.id;
                const isLoading = loadingPreview === voice.id;
                return (
                  <div
                    key={voice.id}
                    className={cn(
                      'flex cursor-pointer items-center gap-3 rounded-xl border p-2.5 transition-colors duration-150 hover:bg-background',
                      isSelected ? 'border-blue-500/30 bg-blue-500/10' : 'border-border bg-card/60',
                    )}
                    onClick={() => selectVoice(voice.id)}
                  >
                    <div
                      className={cn(
                        'flex h-9 w-9 shrink-0 items-center justify-center rounded-full',
                        voice.gender === 'female' ? 'bg-pink-500/10 text-pink-400' : 'bg-blue-500/10 text-blue-400',
                      )}
                    >
                      {voice.gender === 'female' ? <UserCircle className="h-5 w-5" /> : <User className="h-5 w-5" />}
                    </div>

                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5">
                        <span className="text-sm font-medium text-foreground">{voice.name}</span>
                        {isSelected ? <Check className="h-3.5 w-3.5 shrink-0 text-blue-300" /> : null}
                      </div>
                      <p className="truncate text-xs text-muted-foreground">{voice.style}</p>
                    </div>

                    <button
                      type="button"
                      onClick={(event) => {
                        event.stopPropagation();
                        void playPreview(voice.id);
                      }}
                      disabled={isLoading}
                      className={cn(
                        'flex h-9 w-9 shrink-0 items-center justify-center rounded-full transition-colors duration-150 hover:bg-background',
                        isPlaying && 'bg-blue-500/10 text-blue-300',
                        isLoading && 'opacity-60',
                      )}
                      title={isPlaying ? 'Stop preview' : 'Preview voice'}
                      aria-label={isPlaying ? `Stop preview for ${voice.name}` : `Preview ${voice.name}`}
                    >
                      {isLoading ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : isPlaying ? (
                        <Square className="h-3.5 w-3.5 fill-current" />
                      ) : (
                        <Play className="h-3.5 w-3.5 fill-current" />
                      )}
                    </button>
                  </div>
                );
              })
            ) : (
              <div className="py-8 text-center text-sm text-muted-foreground">No voices available right now.</div>
            )}
          </div>

          <div className="border-t border-border bg-background px-4 py-2.5">
            <p className="text-center text-xs text-muted-foreground">
              ✓ Using <span className="font-medium text-foreground">{selectedVoiceName}</span> voice
            </p>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default VoicePicker;
